/**
 * The sidecar next to every assembled mech (`assets/processed/mechs/<id>/mech.json`, Phase 6):
 * what the client needs to know about the model beyond the glTF itself. Written by the asset
 * pipeline from the assembler's report and parsed by the client before the model is used.
 */
import { z } from 'zod';
const id = z.string().regex(/^[a-z0-9][a-z0-9-]*$/);
const hex = z.string().regex(/^#[0-9a-f]{6}$/);
const vec3 = z.tuple([z.number(), z.number(), z.number()]);
export const MechLiveryDefaultsSchema = z
    .object({
    primary: hex,
    secondary: hex,
    accent: hex,
    emissive: hex,
    /** How worn the paint looks by default, 0..1. */
    wear: z.number().min(0).max(1),
    decal: z.object({ id, tint: hex }).strict().nullable(),
})
    .strict();
export const MechSidecarSchema = z
    .object({
    version: z.literal(1),
    mech: id,
    body: id,
    /** Uniform scale the whole rig was exported at. */
    scale: z.number().positive(),
    /** Body-only height (feet to crown), which the gameplay capsule follows. */
    heightMetres: z.number().positive(),
    /** Everything including masts and antennae; the hangar camera frames this. */
    boundsHeight: z.number().positive(),
    /** Node names per LOD level (the body and the kit meshes of that level). */
    lods: z
        .array(z
        .object({
        level: z.number().int().min(0).max(2),
        nodes: z.array(z.string().min(1)).min(1),
    })
        .strict())
        .min(1),
    /** `SOCKET_<name>` empties: which bone they ride and where they sit in the rest pose. */
    sockets: z.record(z.string(), z.object({ bone: z.string().min(1), position: vec3 }).strict()),
    weapons: z
        .object({
        primary: z.string().nullable(),
        secondary: z.string().nullable(),
        /** The `WEAPON_ALT_primary` node's weapon id (Phase 8), exported hidden. */
        alternatePrimary: z.string().nullable().default(null),
    })
        .strict(),
    /** Node names of the optional cosmetic parts, exported hidden. */
    cosmetics: z.array(z.string().min(1)),
    livery: MechLiveryDefaultsSchema,
})
    .strict();
//# sourceMappingURL=mech-sidecar.js.map